import { z } from 'zod';
import { phoneSchema } from './common';

/**
 * Zod schemas для drawer'а профиля (AK-229) и password reset (AK-230).
 *
 * Backend источник: backend/src/ikolhoz/schemas/auth.py
 *   - ProfileUpdateIn  → updateProfileSchema
 *   - PasswordChangeIn → changePasswordSchema
 *   - EmailChangeRequestIn → changeEmailSchema
 *   - PasswordResetRequestIn → forgotPasswordSchema
 *   - PasswordResetConfirmIn → resetPasswordSchema
 *
 * Цель — поймать ошибку **до** submit (Nielsen H5 — Error prevention),
 * чтобы юзер не дёргал backend на тривиальных промахах (пустой пароль,
 * email без @, разные пароли в форме сброса).
 */

export const updateProfileSchema = z.object({
  first_name: z
    .string()
    .min(1, 'Имя обязательно')
    .max(100, 'Не более 100 символов'),
  last_name: z
    .string()
    .min(1, 'Фамилия обязательна')
    .max(100, 'Не более 100 символов'),
  middle_name: z
    .string()
    .max(100, 'Не более 100 символов')
    .optional()
    .or(z.literal('')),
  phone: phoneSchema,
  preferred_language: z.string().regex(/^[a-z]{2}$/, 'Код языка: 2 буквы'),
  timezone: z.string().min(1, 'Таймзона обязательна'),
});
export type UpdateProfileForm = z.infer<typeof updateProfileSchema>;

/** Смена пароля из drawer'а (требует текущий пароль). */
/**
 * Схема установки первого пароля: поля «текущий» нет вовсе.
 *
 * У приглашённого пользователя `password_hash` пустой, и требовать «текущий
 * пароль» значило бы требовать несуществующее — форма запиралась навсегда
 * (бэкенд такой случай как раз разрешает, см. `profile.change_password`).
 */
export function passwordFormSchema(requireCurrent: boolean) {
  return z
    .object({
      // Набор полей один в обоих режимах — меняется только обязательность.
      // Разные формы значений заставили бы react-hook-form переинициализировать
      // форму при переключении режима и стоили бы отдельного типа наружу.
      current_password: requireCurrent
        ? z.string().min(1, 'Текущий пароль обязателен')
        : z.string(),
      new_password: z.string().min(8, 'Минимум 8 символов').max(200),
      confirm_password: z.string().min(1, 'Подтвердите пароль'),
    })
    .refine((data) => data.new_password === data.confirm_password, {
      path: ['confirm_password'],
      message: 'Пароли не совпадают',
    });
}

export const changePasswordSchema = z
  .object({
    current_password: z.string().min(1, 'Текущий пароль обязателен'),
    new_password: z.string().min(8, 'Минимум 8 символов').max(200),
    confirm_password: z.string().min(1, 'Подтвердите новый пароль'),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    path: ['confirm_password'],
    message: 'Пароли не совпадают',
  });
export type ChangePasswordForm = z.infer<typeof changePasswordSchema>;

/** Смена email (запрос — backend подтвердит письмом). */
export const changeEmailSchema = z.object({
  new_email: z
    .string()
    .min(3, 'Email обязателен')
    .email('Введите корректный email')
    .max(200),
  current_password: z.string().min(1, 'Текущий пароль обязателен'),
});
export type ChangeEmailForm = z.infer<typeof changeEmailSchema>;

/** AK-230 — забыли пароль (email-only). */
export const forgotPasswordSchema = z.object({
  email: z.string().min(1, 'Email обязателен').email('Введите корректный email'),
});
export type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>;

/** AK-230 — установить новый пароль по токену из письма. */
export const resetPasswordSchema = z
  .object({
    new_password: z.string().min(8, 'Минимум 8 символов').max(200),
    confirm_password: z.string().min(1, 'Подтвердите пароль'),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    path: ['confirm_password'],
    message: 'Пароли не совпадают',
  });
export type ResetPasswordForm = z.infer<typeof resetPasswordSchema>;
